<template>
  <div>
    <!-- FILTER SECTION -->
    <!-- Date filters for narrowing payment logs by selected range -->

    <div class="row mb-3">
      <div class="col-md-3">
        <label for="payments-date-range-batch">Batch</label>
        <b-form-select
          id="payments-date-range-batch"
          v-model="filters.batch"
          :options="batchOptions"
        />
      </div>

      <div class="col-md-3">
        <label for="payments-date-range-intake">Intake</label>
        <b-form-select
          id="payments-date-range-intake"
          v-model="filters.intake"
          :options="intakeOptions"
        />
      </div>

      <div class="col-md-3">
        <label for="payments-date-range-from">From Date</label>

        <input
          id="payments-date-range-from"
          v-model="filters.from_date"
          type="date"
          class="form-control"
        >
      </div>

      <div class="col-md-3">
        <label for="payments-date-range-to">To Date</label>

        <input
          id="payments-date-range-to"
          v-model="filters.to_date"
          type="date"
          class="form-control"
        >
      </div>

    </div>

    <div class="row justify-content-end mt-2 mb-3">
      <div class="col-12 col-sm-auto mb-2 mb-sm-0">
        <button
          class="btn btn-primary px-3"
          @click="getPayments"
        >
          Go
        </button>
      </div>
      <div class="col-12 col-sm-auto">
        <button
          class="btn btn-success px-3"
          @click="getTodayPayments"
        >
          Today's Payments
        </button>
      </div>
    </div>

    <!-- TABLE -->
    <!-- Payment results table -->

    <b-card body-class="p-0">
      <div class="d-flex justify-content-end px-2 py-2 mb-2 font-weight-bold border-bottom">
        Total Amount: {{ getPrice(totalAmount) }}
      </div>
      <b-table
        v-if="filteredPayments.length !== 0"
        responsive="sm"
        bordered
        hover
        :items="filteredPayments"
        :fields="fields"
        class="mb-0"
      >
        <template #cell(batch)="data">
          {{ getPaymentBatch(data.item) }}
        </template>

        <template #cell(intake)="data">
          {{ getPaymentIntake(data.item) }}
        </template>

        <template #cell(payment_date)="data">
          {{ momentTimestampFormat(data.value, "YYYY-MM-DD") }}
        </template>

        <template #cell(amount)="data">
          <div class="text-right">
            {{ getPrice(data.value) }}
          </div>
        </template>

        <!-- Adding View invoice link -->
        <!-- Opens the invoice for the selected payment row -->

        <template #cell(actions)="data">
            <b-link @click="viewInvoice(data.item)">
              View Invoice
            </b-link>
          </template>


      </b-table>

      <div
        v-else
        class="text-center py-2"
      >
        <span class="font-weight-bold">No Payments Found</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import paymentApi from '@/Api/Modules/payment'
import usersApi from '@/Api/Modules/auth'
import { BCard, BFormSelect, BTable } from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BFormSelect,
    BTable,
  },
  data() {
    return {
      filters: {
        from_date: '',
        to_date: '',
        batch: null,
        intake: null,
      },
      batchOptions: [
        { value: null, text: 'All Batches' },
        ...['B03', 'B04', 'B05', 'B06', 'B07'].map(value => ({ value, text: value })),
      ],
      intakeOptions: [
        { value: null, text: 'All Intakes' },
        ...['01', '02'].map(value => ({ value, text: value })),
      ],

      payments: [],
      users: [],
      fields: [
        {
          key: 'batch',
          label: 'Batch',
          sortable: true,
        },
        {
          key: 'intake',
          label: 'Intake',
          sortable: true,
        },
        {
          key: 'invoice_no',
          label: 'Invoice Number',
          sortable: true,
        },
        {
          key: 'payment_date',
          label: 'Paid Date',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Student Name',
          sortable: true,
        },
        {
          key: 'amount',
          label: 'Amount',
          sortable: true,
          tdClass: 'text-right',
        },

             {
          key: 'actions',
          label: 'Invoice',
        },
      ],
    }
  },

  computed: {
    filteredPayments() {
      return this.payments.filter(payment => {
        if (!this.filters.batch && !this.filters.intake) return true

        return this.findMatchingUsers(payment).some(user => {
          const matchesBatch = !this.filters.batch || String(user.batch) === String(this.filters.batch)
          const matchesIntake = !this.filters.intake || String(user.intake) === String(this.filters.intake)

          return matchesBatch && matchesIntake
        })
      })
    },
    totalAmount() {
      return this.filteredPayments.reduce((total, payment) => total + this.toAmount(payment.amount), 0)
    },
  },

  created() {
    this.getPayments()
  },
  methods: {
    findMatchingUsers(payment) {
      const paymentName = (payment.name || '').trim().toLowerCase()

      return this.users.filter(user => (user.name || '').trim().toLowerCase() === paymentName)
    },
    getPaymentBatch(payment) {
      const batches = [...new Set(this.findMatchingUsers(payment).map(user => user.batch).filter(Boolean))]

      return batches.length === 1 ? batches[0] : 'N/A'
    },
    getPaymentIntake(payment) {
      const intakes = [...new Set(this.findMatchingUsers(payment).map(user => user.intake).filter(Boolean))]

      return intakes.length === 1 ? intakes[0] : 'N/A'
    },
    toAmount(value) {
      const amount = Number(value)

      return Number.isFinite(amount) ? amount : 0
    },
    getPrice(value) {
      return `Rs. ${this.toAmount(value).toFixed(2)}`
    },
    // get payments
    // Load payments using the currently selected date filters.
    async getPayments() {
      try {
        const [response, usersResponse] = await Promise.all([
          paymentApi.getpaymentlogs({
            from_date: this.filters.from_date,
            to_date: this.filters.to_date,
          }),
          this.users.length ? Promise.resolve(null) : usersApi.allUsers(),
        ])

        this.payments = Array.isArray(response.data) ? response.data : ((response.data && response.data.data) || [])
        if (usersResponse) this.users = usersResponse.data.data || []
      } catch (error) {
        console.error(error)
      }
    },

    // get today payments
    // Set the filter range to today and reload payment results.
    async getTodayPayments() {
      try {
        const today = new Date().toISOString().split('T')[0]

        this.filters.from_date = today
        this.filters.to_date = today

        const [response, usersResponse] = await Promise.all([
          paymentApi.getpaymentlogs({
            from_date: this.filters.from_date,
            to_date: this.filters.to_date,
          }),
          this.users.length ? Promise.resolve(null) : usersApi.allUsers(),
        ])

        this.payments = Array.isArray(response.data) ? response.data : ((response.data && response.data.data) || [])
        if (usersResponse) this.users = usersResponse.data.data || []

        this.searched = true
      } catch (error) {
        console.error(error)
      }
    },

    // Open the selected payment invoice in a new browser tab.

        viewInvoice(item) {
      const invoiceData = {
        id: item.id,
        student: item.student || 'Unknown',
        date: item.payment_date,
        amount: item.amount,
        paid: item.amount, // current payment
        total: item.total || 30000, // adjust if you have this
        due: item.due || 0,
      }
      console.log('Invoice clicked:', item)

      window.open(`/invoice/${item.id}`, '_blank')

      // Example 2: Open in new tab
      // window.open(`/invoice/${item.id}`, "_blank");
    },
  },
}
</script>
